/* eslint-disable @typescript-eslint/no-explicit-any */

import { Box, Text, Tooltip, VStack, HStack, Divider } from '@chakra-ui/react';
import { FC } from 'react';

interface CustomizedFieldsTooltipProps {
  customizedFields: any;
  category: string;
  children: React.ReactNode;
}

const CustomizedFieldsTooltip: FC<CustomizedFieldsTooltipProps> = ({
  customizedFields,
  category,
  children,
}) => {
  const measurements = customizedFields?.measurements;
  const unit = customizedFields?.unit;

  const categoryLower = category?.toLowerCase() || '';
  const isKurta = categoryLower.includes('kurta');
  // const isSareeBlouse = categoryLower.includes('blouse') || categoryLower.includes('saree');
  const isLehenga = categoryLower.includes('lehenga');

  const formatFieldName = (key: string) =>
    key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

  // Render any field (measurements)
  const renderMeasurement = (key: string, value: unknown) => {
    if (!value || value === '') return null;
    if (typeof value === 'string' || typeof value === 'number') {
      return (
        <Text key={key} fontSize="xs" color="text.primary">
          <Text as="span" fontWeight="medium">
            {formatFieldName(key)}:
          </Text>{' '}
          {value} {unit}
        </Text>
      );
    }
    return null;
  };

  // Render blouse_style (supports string[] or {name: string}[])
  const renderBlouseStyle = () => {
    const value = customizedFields.blouse_style;
    if (!value || (Array.isArray(value) && value.length === 0)) return null;

    const styles = Array.isArray(value)
      ? value.map((item) => (typeof item === 'string' ? item : item?.name)).filter(Boolean)
      : [];

    if (styles.length === 0) return null;

    return (
      <Text fontSize="xs" color="text.primary" fontWeight="medium">
        Blouse Style: {styles.join(', ')}
      </Text>
    );
  };

  // Render padding
  const renderPadding = () => {
    if (customizedFields.is_padding_required === undefined) return null;
    return (
      <Text fontSize="xs" color="text.primary" fontWeight="medium">
        Padding Required: {customizedFields.is_padding_required ? 'Yes' : 'No'}
      </Text>
    );
  };

  // Render a section (Upper / Lower)
  const renderSection = (title: string, data: Record<string, unknown> | undefined) => {
    if (!data || typeof data !== 'object') return null;

    const fields = Object.entries(data)
      .map(([k, v]) => renderMeasurement(k, v))
      .filter(Boolean);

    if (fields.length === 0) return null;

    return (
      <VStack align="start" spacing={2} flex={1} minW="150px">
        <Text fontWeight="bold" fontSize="sm" color="text.primary">
          {title}
        </Text>
        <VStack align="start" spacing={1.5} w="100%">
          {fields}
        </VStack>
      </VStack>
    );
  };

  const getTooltipContent = () => {
    const hasMeasurements = measurements && Object.keys(measurements).length > 0;
    const hasBlouseStyle = !!renderBlouseStyle();
    const hasPadding = !!renderPadding();

    if (!hasMeasurements && !hasBlouseStyle && !hasPadding) {
      return (
        <Text fontSize="xs" color="gray.500">
          No customizations
        </Text>
      );
    }

    // Lehenga: Upper & Lower side-by-side, style + padding at bottom
    if (isLehenga) {
      const upper = renderSection('Upper Body', measurements?.upper);
      const lower = renderSection('Lower Body', measurements?.lower);

      return (
        <VStack align="stretch" spacing={4} w="100%">
          {/* Upper & Lower Side by Side */}
          {(upper || lower) && (
            <HStack
              align="start"
              spacing={{ base: 6, md: 10 }}
              w="100%"
              flexDirection={{ base: 'column', md: 'row' }} // ← Mobile: stacked, Desktop: side-by-side
              gap={{ base: 4, md: 8 }}
            >
              {upper}
              {lower}
            </HStack>
          )}

          {/* Blouse Style & Padding at the Bottom */}
          {(hasBlouseStyle || hasPadding) && (
            <>
              <Divider />
              <VStack align="start" spacing={1.5}>
                {renderBlouseStyle()}
                {renderPadding()}
              </VStack>
            </>
          )}
        </VStack>
      );
    }

    // Kurta / Saree Blouse: Normal list + style & padding at bottom
    const fieldsToShow = isKurta
      ? ['chest', 'waist', 'length', 'hip', 'shoulder_width', 'sleeve_length']
      : [
          'bust',
          'underbust',
          'waist',
          'shoulder_width',
          'blouse_length',
          'armhole',
          'sleeve_length',
        ];

    const mainFields = fieldsToShow
      .map((key) =>
        renderMeasurement(key, measurements ? (measurements as any)[key] : customizedFields[key]),
      )
      .filter(Boolean);

    return (
      <VStack align="start" spacing={4}>
        {/* Main measurements */}
        {mainFields.length > 0 && (
          <VStack align="start" spacing={1.5} w="100%">
            {mainFields}
          </VStack>
        )}

        {/* Style & Padding at bottom */}
        {(hasBlouseStyle || hasPadding) && (
          <>
            <Divider />
            <VStack align="start" spacing={1.5}>
              {renderBlouseStyle()}
              {renderPadding()}
            </VStack>
          </>
        )}
      </VStack>
    );
  };

  return (
    <Tooltip
      label={
        <Box p={4} minW="340px" maxW="520px" bg="background.primary" borderRadius="md">
          {getTooltipContent()}
        </Box>
      }
      bg="background.primary"
      color="text.secondary"
      fontSize="xs"
      hasArrow
      placement="bottom"
      arrowSize={10}
    >
      {children}
    </Tooltip>
  );
};

export default CustomizedFieldsTooltip;
